/**
 * Delivery Dash (owner, Sep 28: "a lane-dodging courier run through traffic") — a phone game (`game:delivery_dash`): the courier's
 * scooter up a three-lane street, the traffic slower than you, parcels lying in the lanes. ← → (a swipe, or a tap either side of the
 * scooter) hops a lane; the street gets quicker the further you go. One crash and the run is over.
 */
import { clamp, core, end, pop, rint, rnd, tick, L, R, type Core, type MiniGame } from './core';
import { box, carTop, disc, halftone, hud, INK, pops, shout } from './draw';

const W = 180, H = 320, BIKE_Y = 262, LANES = [46, 90, 134], SWITCH_S = 0.11;
interface Car { pos: number; lane: number; v: number; len: number; c: string }
export interface Dash extends Core { d: number; speed: number; lane: number; from: number; sw: number; cars: Car[]; parcels: { pos: number; lane: number }[]; next: number; bonus: number; got: number }
/** the traffic's one speed: a row keeps its shape all the way down, so the gap it leaves stays a gap */
const TRAFFIC_V = 50;
const COLORS = ['#3a8ee8', '#e8e4d8', '#4ec27a', '#9a5ad8', '#e8413a', '#2a2a32'];

/** the scooter's x now (between two lanes while it hops) */
export function bikeX(s: Dash): number { const k = s.sw > 0 ? 1 - s.sw / SWITCH_S : 1; return LANES[s.from] + (LANES[s.lane] - LANES[s.from]) * k; }
/** a thing at world position `pos` is this far above the scooter on the screen */
const screenY = (s: Dash, pos: number): number => BIKE_Y - (pos - s.d);
export { LANES as DASH_LANES };

export const dash: MiniGame<Dash> = {
  id: 'delivery_dash', name: 'Delivery Dash', icon: '🛵', color: '#d0701a', where: 'phone', unlock: 'game:delivery_dash',
  blurb: 'The courier’s run: dodge the traffic, grab the parcels.',
  keys: '← → change lane', touch: 'swipe, or tap left / right of the scooter',
  w: W, h: H, maxRate: 300, maxScore: 2000000,
  init(seed) { const s = core(seed) as Dash; Object.assign(s, { d: 0, speed: 150, lane: 1, from: 1, sw: 0, cars: [], parcels: [], next: 220, bonus: 0, got: 0 }); return s; },
  step(s, inp, dt) {
    tick(s, dt); if (s.over) return;
    s.speed = Math.min(380, 150 + s.t * 3.2);
    s.d += s.speed * dt;
    if (s.sw > 0) s.sw = Math.max(0, s.sw - dt);
    let want = 0;
    if (inp.press & L) want = -1; else if (inp.press & R) want = 1;
    else if (inp.ptap && inp.px >= 0) want = inp.px < bikeX(s) - 8 ? -1 : inp.px > bikeX(s) + 8 ? 1 : 0;
    if (want) { const to = clamp(s.lane + want, 0, 2); if (to !== s.lane) { s.from = s.lane; s.lane = to; s.sw = SWITCH_S; s.fx.push('blip'); } }
    // the traffic: one or two cars a row (never all three lanes), a parcel now and then in a lane they leave
    if (s.d > s.next) {
      const lanes = [0, 1, 2], n = rnd(s) < 0.3 + Math.min(0.4, s.t / 120) ? 2 : 1;
      for (let i = 0; i < n; i++) { const lane = lanes.splice(rint(s, 0, lanes.length - 1), 1)[0], len = rnd(s) < 0.15 ? 44 : 26; s.cars.push({ pos: s.d + H, lane, v: TRAFFIC_V, len, c: len > 30 ? '#f2c230' : COLORS[rint(s, 0, COLORS.length - 1)] }); }
      if (rnd(s) < 0.5) s.parcels.push({ pos: s.d + H + 50, lane: lanes[rint(s, 0, lanes.length - 1)] });
      s.next = s.d + s.speed * 0.6 + 60 + rnd(s) * 90;
    }
    for (const c of s.cars) c.pos += c.v * dt;
    s.cars = s.cars.filter((c) => screenY(s, c.pos) < H + 60);
    s.parcels = s.parcels.filter((p) => screenY(s, p.pos) < H + 30);
    const bx = bikeX(s);
    for (const c of s.cars) if (Math.abs(LANES[c.lane] - bx) < 17 && Math.abs(screenY(s, c.pos) - BIKE_Y) < c.len / 2 + 9) { s.shake = 6; pop(s, bx, BIKE_Y - 20, 'CRASH!', '#ff4a2a'); s.fx.push('boom'); end(s); return; }
    for (const p of s.parcels) if (Math.abs(LANES[p.lane] - bx) < 16 && Math.abs(screenY(s, p.pos) - BIKE_Y) < 16) { s.bonus += 50; s.got++; s.fx.push('coin'); pop(s, bx, BIKE_Y - 22, '+50', '#ffb84a'); p.pos = -1e9; }
    s.score = Math.floor(s.d / 10) + s.bonus;
  },
  draw(ctx, s) {
    const sh = s.shake ? Math.sin(s.t * 80) * s.shake : 0; ctx.save(); ctx.translate(sh, 0);
    // the pavements and the shop fronts sliding down, the street, its lane lines
    ctx.fillStyle = '#c9c2b2'; ctx.fillRect(-6, 0, W + 12, H); halftone(ctx, 0, 0, 22, H, 'rgba(0,0,0,0.1)', 6, 1); halftone(ctx, W - 22, 0, 22, H, 'rgba(0,0,0,0.1)', 6, 1);
    const off = s.d % 70;
    for (let i = -1; i < 6; i++) { const y = i * 70 + off - 70; for (const [x, flip] of [[0, false], [W - 12, true]] as const) { const k = (Math.floor((s.d - off) / 70) - i) & 3; box(ctx, x, y + 6, 12, 58, ['#e8413a', '#3a8ee8', '#4ec27a', '#f2c230'][k], 1, 1.2); ctx.fillStyle = 'rgba(255,255,255,0.7)'; for (let j = 0; j < 4; j++) ctx.fillRect(flip ? x : x + 8, y + 10 + j * 14, 4, 7); } }
    ctx.fillStyle = '#3b3f4a'; ctx.fillRect(22, 0, W - 44, H); ctx.fillStyle = INK; ctx.fillRect(21, 0, 2, H); ctx.fillRect(W - 23, 0, 2, H);
    ctx.fillStyle = '#f4f0e0'; for (const x of [68, 112]) for (let y = (s.d % 36) - 36; y < H; y += 36) ctx.fillRect(x - 1, y, 2, 18);
    for (const p of s.parcels) { const y = screenY(s, p.pos), x = LANES[p.lane]; box(ctx, x - 8, y - 7, 16, 14, '#c88a4a', 1.5); ctx.fillStyle = '#e8c890'; ctx.fillRect(x - 1.5, y - 7, 3, 14); }
    for (const c of s.cars) carTop(ctx, LANES[c.lane], screenY(s, c.pos), c.len, 15, -Math.PI / 2, c.c);
    // the courier: the scooter, the rider, the big box on the back
    const bx = bikeX(s), tilt = s.sw > 0 ? (s.lane > s.from ? 1 : -1) * 0.25 : 0;
    ctx.save(); ctx.translate(bx, BIKE_Y); ctx.rotate(tilt);
    ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.beginPath(); ctx.ellipse(2, 4, 8, 16, 0, 0, Math.PI * 2); ctx.fill();
    box(ctx, -3, -16, 6, 32, '#222', 3, 1.2); box(ctx, -6, -8, 12, 16, '#e8413a', 4); box(ctx, -8, 4, 16, 14, '#ffb84a', 2); shout(ctx, 'H', 0, 11.5, 8, '#fff'); disc(ctx, 0, -5, 5, '#f2c230', 1.3);
    ctx.restore();
    ctx.restore();
    pops(ctx, s);
    hud(ctx, s, W, `📦 ${s.got}`);
  },
};
